import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, CheckCircle, Zap, Shield, TrendingUp, MessageCircle } from 'lucide-react';
import { companyInfo, subscriptionPlans, aboutStats } from '../data/mock';
import { Button } from '../components/ui/button';
import EconomyPlanCalculator from '../components/EconomyPlanCalculator';
import ServiceAreas from '../components/ServiceAreas';

const Home = () => {
  const navigate = useNavigate();

  const handlePlanSelect = (planName) => {
    navigate('/apply', { state: { selectedPlan: planName } });
  };

  const handleMessengerInquiry = () => {
    window.open(companyInfo.messenger, '_blank');
  };

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.pexels.com/photos/4508751/pexels-photo-4508751.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
            alt="Data Center Infrastructure"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-slate-950/95 via-slate-900/90 to-cyan-950/80"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <div className="text-center space-y-8">
            <div className="inline-block">
              <span className="px-4 py-2 bg-cyan-500/10 border border-cyan-500/20 rounded-full text-cyan-400 text-sm font-medium backdrop-blur-sm">
                Carrier-Grade ISP Solutions
              </span>
            </div>
            
            <h1 className="text-5xl md:text-7xl font-bold text-white leading-tight">
              Bridging the
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">
                Digital Divide
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              {companyInfo.description}
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
              <Link to="/franchise">
                <Button className="bg-cyan-500 hover:bg-cyan-600 text-white px-8 py-6 text-lg rounded-xl shadow-lg shadow-cyan-500/20 transition-all duration-200 hover:shadow-cyan-500/40 hover:scale-105">
                  Explore Franchise Opportunities
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="border-2 border-gray-600 text-white hover:bg-slate-800 px-8 py-6 text-lg rounded-xl transition-all duration-200">
                  Contact Sales
                </Button>
              </Link>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 pt-16">
              {aboutStats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-4xl md:text-5xl font-bold text-cyan-400 mb-2">
                    {stat.value}
                  </div>
                  <div className="text-sm text-gray-400">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-gray-600 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-3 bg-cyan-400 rounded-full"></div>
          </div>
        </div>
      </section>

      {/* Key Features Section */}
      <section className="py-24 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Why Choose HVGTEC?
            </h2>
            <p className="text-xl text-gray-400">
              Enterprise-grade infrastructure designed for the Philippines
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-2xl p-8 hover:border-cyan-500/50 transition-all duration-300 hover:shadow-lg hover:shadow-cyan-500/10">
              <div className="w-14 h-14 bg-cyan-500/10 rounded-xl flex items-center justify-center mb-6">
                <Zap className="text-cyan-400" size={28} />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Carrier-Grade Reliability</h3>
              <p className="text-gray-400 leading-relaxed mb-4">
                Built with Ubiquiti, MikroTik, and Juniper equipment. OSPF, BGP, and VPLS protocols ensure 99.9% uptime.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center text-gray-300 text-sm">
                  <CheckCircle className="text-cyan-400 mr-2" size={16} />
                  Enterprise routing protocols
                </li>
                <li className="flex items-center text-gray-300 text-sm">
                  <CheckCircle className="text-cyan-400 mr-2" size={16} />
                  Redundant network paths
                </li>
                <li className="flex items-center text-gray-300 text-sm">
                  <CheckCircle className="text-cyan-400 mr-2" size={16} />
                  24/7 NOC monitoring
                </li>
              </ul>
            </div>

            <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-2xl p-8 hover:border-cyan-500/50 transition-all duration-300 hover:shadow-lg hover:shadow-cyan-500/10">
              <div className="w-14 h-14 bg-gradient-to-br from-cyan-500/20 to-orange-500/20 rounded-xl flex items-center justify-center mb-6">
                <svg xmlns="http://www.w3.org/2000/svg" className="text-cyan-400" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2v4" />
                  <path d="m6.34 6.34 2.83 2.83" />
                  <path d="M2 12h4" />
                  <path d="m6.34 17.66 2.83-2.83" />
                  <path d="M12 18v4" />
                  <path d="m17.66 17.66-2.83-2.83" />
                  <path d="M18 12h4" />
                  <path d="m17.66 6.34-2.83 2.83" />
                  <circle cx="12" cy="12" r="4" />
                  <path d="M12 12 L8 16" className="text-orange-400" />
                  <path d="M4 20 L8 16" className="text-orange-400" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Multi-Path Hybrid Resilience</h3>
              <p className="text-gray-400 leading-relaxed mb-4">
                Built from years of operating in harsh Philippine conditions—engineered to stay online when others fail.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center text-gray-300 text-sm">
                  <CheckCircle className="text-cyan-400 mr-2" size={16} />
                  <span><strong className="text-cyan-300">Hybrid Power:</strong> Grid + Solar + LiFePO4</span>
                </li>
                <li className="flex items-center text-gray-300 text-sm">
                  <CheckCircle className="text-cyan-400 mr-2" size={16} />
                  <span><strong className="text-cyan-300">Fiber-Wireless Overlay:</strong> Dual-path redundancy</span>
                </li>
                <li className="flex items-center text-gray-300 text-sm">
                  <CheckCircle className="text-cyan-400 mr-2" size={16} />
                  <span><strong className="text-cyan-300">Hazard-Averse:</strong> Wireless backhauls for long spans</span>
                </li>
              </ul>
            </div>

            <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-2xl p-8 hover:border-cyan-500/50 transition-all duration-300 hover:shadow-lg hover:shadow-cyan-500/10">
              <div className="w-14 h-14 bg-cyan-500/10 rounded-xl flex items-center justify-center mb-6">
                <TrendingUp className="text-cyan-400" size={28} />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Proven ROI Models</h3>
              <p className="text-gray-400 leading-relaxed mb-4">
                From 120-client Micro POPs to 4,000+ Regional Cores with clear revenue projections.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center text-gray-300 text-sm">
                  <CheckCircle className="text-cyan-400 mr-2" size={16} />
                  12-24 month ROI period
                </li>
                <li className="flex items-center text-gray-300 text-sm">
                  <CheckCircle className="text-cyan-400 mr-2" size={16} />
                  Scalable franchise models
                </li>
                <li className="flex items-center text-gray-300 text-sm">
                  <CheckCircle className="text-cyan-400 mr-2" size={16} />
                  Full technical support
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Subscription Plans Section */}
      <section className="py-24 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              FiberStream FTTH Plans
            </h2>
            <p className="text-xl text-gray-400">
              High-speed fiber internet for Southern Leyte by HVGTEC
            </p>
          </div>

          {/* Premium Plans */}
          <div className="mb-12">
            <div className="flex items-center justify-center mb-8">
              <div className="px-6 py-2 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border border-cyan-500/30 rounded-full">
                <span className="text-cyan-400 font-semibold text-lg">Premium Plans</span>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {subscriptionPlans.premium.map((plan) => (
                <div 
                  key={plan.id}
                  className={`relative bg-slate-900 border rounded-2xl p-8 transition-all duration-300 hover:scale-105 ${
                    plan.popular 
                      ? 'border-cyan-500 shadow-lg shadow-cyan-500/20' 
                      : 'border-slate-700 hover:border-cyan-500/50'
                  }`}
                >
                  {plan.popular && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <span className="bg-cyan-500 text-white px-4 py-1 rounded-full text-sm font-semibold">
                        Most Popular
                      </span>
                    </div>
                  )}
                  
                  <div className="text-center mb-6">
                    <h3 className="text-2xl font-bold text-white mb-2">{plan.name}</h3>
                    <p className="text-gray-400 text-sm mb-4">{plan.description}</p>
                    <div className="text-5xl font-bold text-cyan-400 mb-2">
                      {plan.price}
                      <span className="text-xl text-gray-500">/mo</span>
                    </div>
                    <div className="text-xl text-gray-300">{plan.speed}</div>
                  </div>

                  <ul className="space-y-3 mb-6">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start text-gray-300">
                        <CheckCircle className="text-cyan-400 mr-2 mt-0.5 flex-shrink-0" size={18} />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Messenger Button - Primary */}
                  <Button 
                    onClick={handleMessengerInquiry}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 rounded-xl transition-all duration-200 mb-3 shadow-lg shadow-blue-600/20 hover:shadow-blue-600/40"
                  >
                    <MessageCircle className="mr-2" size={20} />
                    Inquire via Messenger
                  </Button>

                  {/* Apply Button - Secondary */}
                  <Button 
                    onClick={() => handlePlanSelect(plan.name)}
                    className={`w-full py-6 rounded-xl transition-all duration-200 ${
                      plan.popular
                        ? 'bg-cyan-500 hover:bg-cyan-600 text-white'
                        : 'bg-slate-800 hover:bg-slate-700 text-white'
                    }`}
                  >
                    Apply Now
                  </Button>
                </div>
              ))}
            </div>
          </div>

          {/* Economy Plans */}
          <div>
            <div className="flex items-center justify-center mb-8">
              <div className="px-6 py-2 bg-slate-800/50 border border-slate-600 rounded-full">
                <span className="text-gray-300 font-semibold text-lg">Economy Plans</span>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {subscriptionPlans.economy.map((plan) => (
                <div 
                  key={plan.id}
                  className="relative bg-slate-900 border border-slate-700 rounded-2xl p-8 transition-all duration-300 hover:scale-105 hover:border-cyan-500/50"
                >
                  <div className="text-center mb-6">
                    <h3 className="text-2xl font-bold text-white mb-2">{plan.name}</h3>
                    <p className="text-gray-400 text-sm mb-4">{plan.description}</p>
                    <div className="text-5xl font-bold text-cyan-400 mb-2">
                      {plan.price}
                      <span className="text-xl text-gray-500">/mo</span>
                    </div>
                    <div className="text-xl text-gray-300">{plan.speed}</div>
                  </div>

                  <ul className="space-y-3 mb-4">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start text-gray-300">
                        <CheckCircle className="text-cyan-400 mr-2 mt-0.5 flex-shrink-0" size={18} />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <EconomyPlanCalculator planPrice={plan.price} planName={plan.name} />

                  {/* Messenger Button - Primary */}
                  <Button 
                    onClick={handleMessengerInquiry}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 rounded-xl transition-all duration-200 mb-3 shadow-lg shadow-blue-600/20 hover:shadow-blue-600/40"
                  >
                    <MessageCircle className="mr-2" size={20} />
                    Inquire via Messenger
                  </Button>

                  {/* Apply Button - Secondary */}
                  <Button 
                    onClick={() => handlePlanSelect(plan.name)}
                    className="w-full bg-slate-800 hover:bg-slate-700 text-white py-6 rounded-xl transition-all duration-200"
                  >
                    Apply Now
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Service Areas / Coverage Map */}
      <ServiceAreas />

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-slate-900 via-cyan-950/30 to-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Transform Your Community?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Partner with HVGTEC and bring reliable, carrier-grade internet to underserved regions.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/franchise">
              <Button className="bg-cyan-500 hover:bg-cyan-600 text-white px-8 py-6 text-lg rounded-xl shadow-lg shadow-cyan-500/20 transition-all duration-200 hover:shadow-cyan-500/40">
                View Franchise Models
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="border-2 border-gray-600 text-white hover:bg-slate-800 px-8 py-6 text-lg rounded-xl transition-all duration-200">
                Schedule Consultation
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
